import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useProjectsPersonnelQuery } from 'components/hooks/projectsPersonnelQueries';

import ModalBox from 'components/application/ModalBox';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import PersonnelDetails from 'components/personnel/PersonnelDetails';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

import { sortRolesByPrimaryAndPosition } from 'components/helpers/personnel';
import { personDisplayName } from 'components/helpers/users';

export default function PersonnelModal({ onClose, personnelId }) {
  const { t: i18nTCommon } = useTranslation('common');

  const { projectId } = useProjectPersonnelContext();

  const { data: domainPersonnel, isSuccess: isDomainPersonnelSuccess } =
    useProjectsPersonnelQuery({
      personnelId,
      projectId,
    });

  if (!isDomainPersonnelSuccess) return null;

  const {
    data: {
      attributes: { email, externalId, firstName, lastName },
      relationships: {
        company: {
          data: { id: companyId },
        },
        lineManager: { data: lineManagerData },
        profilePhoto: { data: profilePhotoData },
        subcontractor: { data: subcontractorData },
      },
    },
    included,
  } = domainPersonnel;

  const profilePhoto =
    profilePhotoData && included.profilePhoto[profilePhotoData.id];

  const company =
    subcontractorData ?
      included.subcontractor[subcontractorData.id]
    : included.company[companyId];

  const sortedRoles = sortRolesByPrimaryAndPosition(
    Object.values(included.userCompanyRole),
    Object.values(included.companyRole),
  );

  const lineManager =
    lineManagerData && included.lineManager[lineManagerData.id];

  const fieldValues = Object.values(included.fieldValue).reduce(
    (object, fieldValue) => {
      const {
        attributes: { value, valueType },
        relationships: {
          fieldAttribute: {
            data: { id: fieldAttributeId },
          },
          fieldOption: { data: fieldOptionData },
        },
      } = fieldValue;

      object[fieldAttributeId] = {
        fieldOptionId: fieldOptionData?.id,
        value,
        valueType,
      };

      return object;
    },
    {},
  );

  return (
    <ModalBox isOpen={true} mode='portrait' onClose={onClose}>
      <div className='popup__header popup__header--sm popup__header--secondary tw-bg-mint-500'>
        <div className='popup__porthole popup__porthole--secondary tw-h-21 tw-w-21 tw-border-white tw-bg-mint-500 [&_svg_path]:tw-fill-white'>
          <ProfilePhotoDisplay
            componentSize='lg'
            photoUrl={profilePhoto?.links?.croppedUrl}
            profileInitials={`${firstName[0]}${lastName[0]}`}
          />
        </div>
      </div>
      <div className='popup__body'>
        <div className='popup__body-show--short'>
          <h2 className='popup__body-header truncated-text-container tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {personDisplayName({ attributes: { firstName, lastName } })}
          </h2>
          <h3 className='popup__body-header popup__body-header--sub truncated-text-container tw-text-m tw-font-normal tw-tracking-auto'>
            {company?.attributes.name}
          </h3>
          <div className='popup__body-content tw-pt-0'>
            {sortedRoles.length > 0 && (
              <ul className='pill-list tw-mt-6'>
                {sortedRoles.map((role, index) => (
                  <li key={index}>
                    <span className='pill tw-bg-grey-050'>
                      {role.attributes.position}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <hr className='popup__body-divider' />
          <div>
            <hr className='popup__body-divider' />
            <div className='popup__body-content popup__body-content--scroll'>
              <PersonnelDetails
                currentPersonnel={{ fieldValues }}
                defaultDetails={[
                  {
                    label: i18nTCommon('personnel.personnelId'),
                    text: externalId,
                  },
                  {
                    label: i18nTCommon('shared.email'),
                    text: email,
                  },
                  {
                    label: i18nTCommon('shared.lineManager'),
                    text:
                      lineManager &&
                      personDisplayName({
                        attributes: lineManager.attributes,
                      }),
                  },
                ]}
                isAllVisible={true}
                isCompact={true}
              />
            </div>
          </div>
        </div>
      </div>
    </ModalBox>
  );
}

PersonnelModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  personnelId: PropTypes.string,
};
